// src/ws/SocketProvider.jsx
import React, { createContext, useContext, useEffect, useMemo } from "react";
import { io } from "socket.io-client";
import { resolveCurrentUserId } from "../utils/currentUserId";

const SocketCtx = createContext({ socket: null });
export const useSocket = () => useContext(SocketCtx);

// Lê env em CRA/Vite ou do public/env.js
const readEnv = (k, def) => {
  const node = (typeof process !== "undefined" && process.env) || {};
  const win = (typeof window !== "undefined" && window.__ENV) || {};
  return node[k] ?? win[k] ?? def;
};

export default function SocketProvider({ userId: userIdProp, children }) {
  // resolve userId: prop > storage (resolveCurrentUserId)
  const userId = useMemo(
    () => (userIdProp != null ? userIdProp : resolveCurrentUserId()),
    [userIdProp]
  );

  // Resolve URL base (usa axios baseURL se existir; fallback = origin atual)
  const WS_URL = useMemo(() => {
    // 1) se existir VITE_WS_URL em public/env.js, usa direto
    const explicit = readEnv("VITE_WS_URL", null);
    if (explicit) return explicit;

    // 2) senão, tenta a dica publicada pelo api.js
    try {
      const base = (window.__AXIOS__ && window.__AXIOS__.baseURL) || null;
      return base ? new URL(base).origin : window.location.origin;
    } catch {
      return window.location.origin;
    }
  }, []);

  // Path do Socket.IO -> **tem que casar com app.js (path: "/ws")**
  const WS_PATH = useMemo(() => readEnv("VITE_WS_PATH", "/ws"), []);

  const socket = useMemo(() => {
    if (!userId) {
      console.warn("[WS] userId não informado; conexão não iniciada.");
      return null;
    }

    // URLs de diagnóstico
    const fullPollingURL = `${WS_URL}${WS_PATH}/?EIO=4&transport=polling`;
    const fullWSURL = `${WS_URL}${WS_PATH}/?EIO=4&transport=websocket`;
    console.log("[WS][CFG]", {
      WS_URL,
      WS_PATH,
      userId: String(userId),
      test_polling: fullPollingURL,
      test_websocket: fullWSURL,
    });

    const s = io(WS_URL, {
      path: WS_PATH,
      transports: ["websocket", "polling"], // fallback
      query: { userId: String(userId) }, // backend lê userId daqui
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 8000,
    });

    // debug global
    if (typeof window !== "undefined") {
      window.__SOCK = s;
    }

    return s;
    // reabre conexão quando userId mudar
  }, [WS_URL, WS_PATH, userId]);

  useEffect(() => {
    if (!socket) return;

    // Logs essenciais
    const onConnect = () => {
      console.log(
        "[WS] CONNECTED id=",
        socket.id,
        "query=",
        socket.io?.opts?.query
      );
    };
    const onDisconnect = (reason) =>
      console.warn("[WS] DISCONNECT:", reason);
    const onConnectError = (err) =>
      console.error("[WS] CONNECT_ERROR:", err?.message || err);
    const onReconnAttempt = (n) =>
      console.log("[WS] RECONNECT_ATTEMPT", n);
    const onReconnected = (n) =>
      console.log("[WS] RECONNECTED", n);
    const onReconnError = (err) =>
      console.error("[WS] RECONNECT_ERROR", err?.message || err);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("connect_error", onConnectError);
    socket.on("reconnect_attempt", onReconnAttempt);
    socket.on("reconnect", onReconnected);
    socket.on("reconnect_error", onReconnError);

    // Diagnóstico amplo (pode comentar se começar a poluir)
    socket.onAny((event, ...args) =>
      console.log("[WS][onAny]", event, args?.[0])
    );

    // Visibilidade/janela: força sync quando volta
    const onVis = () => {
      if (
        document.visibilityState === "visible" &&
        socket.disconnected
      ) {
        console.log(
          "[WS] visível novamente -> socket.connect()"
        );
        socket.connect();
      }
    };
    const onFocus = () => {
      if (socket.disconnected) {
        console.log("[WS] window focus -> socket.connect()");
        socket.connect();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onFocus);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("connect_error", onConnectError);
      socket.off("reconnect_attempt", onReconnAttempt);
      socket.off("reconnect", onReconnected);
      socket.off("reconnect_error", onReconnError);
      socket.offAny();
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onFocus);

      // garante encerramento limpo da instância antiga quando userId mudar
      try {
        socket.close();
      } catch {}
    };
  }, [socket]);

  return (
    <SocketCtx.Provider value={{ socket }}>
      {children}
    </SocketCtx.Provider>
  );
}
